import React, { useState, useEffect, useMemo } from 'react';
import client from './api/client';
import { Link } from 'react-router-dom';
import PageHeader from './components/PageHeader';
import { SP_PER_EXTRACTION, EXTRACTOR_NES_TIERS, TYPE_ID_LARGE_SKILL_INJECTOR, TYPE_ID_SKILL_EXTRACTOR } from './utils/eveConstants';
import { formatPrice } from './utils/formatPrice';
import { useFeeSettings } from './hooks/useFeeSettings';
import FeeSettingsInputs from './components/FeeSettingsInputs';
import './components/shared.css';
import './DealAnalysisPage.css';

// https://www.eveonline.com/news/view/re-mastered-value-packs-in-the-nes
const MPTC = 'Multiple Pilot Training Certificate';

// Genius 'Boost': +12 to all attributes = +18 SP/min. 24 days, +10% with Biology V.
// Accelerators don't stack, so a pack's are used back to back.
const ACCELERATOR_SP_PER_HOUR = 1080;
const ACCELERATOR_DAYS = 24 * 1.1;
const ACCELERATOR_SP = ACCELERATOR_SP_PER_HOUR * 24 * ACCELERATOR_DAYS;

// otherItems aren't tradeable, so they add no ISK value
const MASTERY_PACKS = [
  {
    level: 1,
    plex: 300,
    sp: 0,
    omegaDays: 15,
    skillExtractors: 0,
    accelerators: 1,
    sellableItems: [],
    otherItems: [],
  },
  {
    level: 2,
    plex: 600,
    sp: 648_000,
    omegaDays: 30,
    skillExtractors: 0,
    accelerators: 0,
    sellableItems: [],
    otherItems: [],
  },
  {
    level: 3,
    plex: 1200,
    sp: 648_000,
    omegaDays: 60,
    skillExtractors: 0,
    accelerators: 2,
    sellableItems: [],
    otherItems: [],
  },
  {
    level: 4,
    plex: 2500,
    sp: 3 * 648_000,
    omegaDays: 90,
    skillExtractors: 2,
    accelerators: 2,
    sellableItems: [],
    otherItems: [{ name: MPTC, qty: 2 }],
  },
  {
    level: 5,
    plex: 5000,
    sp: 6 * 648_000,
    omegaDays: 180,
    skillExtractors: 10,
    accelerators: 2,
    sellableItems: [],
    otherItems: [{ name: MPTC, qty: 2 }],
  },
];

// Sale prices typed in by the user. NES sales are short, so they expire a week after the last edit.
const PLEX_OVERRIDES_KEY = 'deal_analysis_plex_overrides';
const PLEX_OVERRIDE_TTL_MS = 7 * 24 * 60 * 60 * 1000;

function loadPlexOverrides() {
  try {
    const { savedAt, prices } = JSON.parse(localStorage.getItem(PLEX_OVERRIDES_KEY));
    return Date.now() - savedAt < PLEX_OVERRIDE_TTL_MS ? prices : {};
  } catch {
    return {};
  }
}

function savePlexOverrides(prices) {
  try {
    if (Object.keys(prices).length) {
      localStorage.setItem(PLEX_OVERRIDES_KEY, JSON.stringify({ savedAt: Date.now(), prices }));
    } else {
      localStorage.removeItem(PLEX_OVERRIDES_KEY);
    }
  } catch {}
}

// Overrides hold raw input text; anything that isn't a positive number falls back to list price
const packPlex = (pack, overrides) => Number(overrides[pack.level]) || pack.plex;

// Collect all sellable item type IDs across packs
const SELLABLE_TYPE_IDS = [...new Set(
  MASTERY_PACKS.flatMap(p => p.sellableItems.map(i => i.typeId))
)];

// All type IDs we need prices for: LSI, SE, plus sellable items
const ALL_JITA_TYPE_IDS = [TYPE_ID_LARGE_SKILL_INJECTOR, TYPE_ID_SKILL_EXTRACTOR, ...SELLABLE_TYPE_IDS];

function DealAnalysisPage() {
  const [prices, setPrices] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [plexOverrides, setPlexOverrides] = useState(loadPlexOverrides);

  const setPlexOverride = (level, value) => {
    const next = { ...plexOverrides };
    if (value === null) delete next[level];
    else next[level] = value;
    setPlexOverrides(next);
    savePlexOverrides(next);
  };

  const {
    brokerFee,
    salesTax,
    sccBuy,
    sccSell,
    buyCostMultiplier,
    sellRevenueMultiplier,
  } = useFeeSettings();

  useEffect(() => {
    const load = async () => {
      try {
        const [jitaRes, plexRes] = await Promise.all([
          client.get(`/eve/prices/?type_ids=${ALL_JITA_TYPE_IDS.join(',')}`),
          client.get('/eve/prices/plex/'),
        ]);
        setPrices({
          jita: jitaRes.data.prices,
          plex: plexRes.data,
        });
        setError(null);
      } catch (err) {
        console.error('Error fetching deal analysis prices:', err);
        setError('Failed to load prices. Please try again.');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const results = useMemo(() => {
    if (!prices) return [];

    const plexBuyPrice = prices.plex?.buy_price;
    const lsiSellPrice = prices.jita[`${TYPE_ID_LARGE_SKILL_INJECTOR}`]?.sell_price;
    const seSellPrice = prices.jita[`${TYPE_ID_SKILL_EXTRACTOR}`]?.sell_price;
    if (!plexBuyPrice || !lsiSellPrice || !seSellPrice) return [];

    // Build extractor source options
    const extractorSources = [
      { label: 'Market', iskPerExtractor: seSellPrice * buyCostMultiplier },
      ...EXTRACTOR_NES_TIERS.map(t => ({
        label: `NES ${t.qty}x (${t.plex} PLEX)`,
        iskPerExtractor: (t.plex / t.qty) * plexBuyPrice * buyCostMultiplier,
      })),
    ];

    const rows = [];

    for (const pack of MASTERY_PACKS) {
      const packIskCost = packPlex(pack, plexOverrides) * plexBuyPrice;
      const acceleratorSp = pack.accelerators * ACCELERATOR_SP;
      const equivalentInjectors = (pack.sp + acceleratorSp) / SP_PER_EXTRACTION;
      const extractorsToSell = Math.max(0, pack.skillExtractors - equivalentInjectors);
      const extractorsToBuy = Math.max(0, equivalentInjectors - pack.skillExtractors);

      const injectorRevenue = equivalentInjectors * lsiSellPrice * sellRevenueMultiplier;
      const extraExtractorRevenue = extractorsToSell * seSellPrice * sellRevenueMultiplier;

      // Revenue from sellable items (Hypercores, Resculpt certs, etc.)
      let itemRevenue = 0;
      const itemDetails = [];
      for (const item of pack.sellableItems) {
        const itemPrice = prices.jita[String(item.typeId)]?.sell_price;
        if (itemPrice) {
          const revenue = item.qty * itemPrice * sellRevenueMultiplier;
          itemRevenue += revenue;
          itemDetails.push({ name: item.name, qty: item.qty, unitPrice: itemPrice, revenue });
        }
      }

      const omegaMonths = pack.omegaDays / 30;

      for (const source of extractorSources) {
        const extractorCost = extractorsToBuy * source.iskPerExtractor;
        const totalRevenue = injectorRevenue + extraExtractorRevenue + itemRevenue;
        const netCost = packIskCost - totalRevenue + extractorCost;
        const monthlyCost = netCost / omegaMonths;

        rows.push({
          pack,
          extractorSource: source.label,
          packIskCost,
          acceleratorSp,
          equivalentInjectors,
          extractorsToBuy,
          extractorsToSell,
          injectorRevenue,
          extraExtractorRevenue,
          extractorCost,
          itemRevenue,
          itemDetails,
          totalRevenue,
          netCost,
          omegaMonths,
          monthlyCost,
        });
      }
    }

    return rows;
  }, [prices, buyCostMultiplier, sellRevenueMultiplier, plexOverrides]);

  // Group results by pack for the detail cards
  const packGroups = useMemo(() => {
    const groups = {};
    for (const row of results) {
      const key = row.pack.level;
      if (!groups[key]) groups[key] = [];
      groups[key].push(row);
    }
    return groups;
  }, [results]);

  return (
    <div className="page">
      <PageHeader title="Deal Analysis" />
      <div className="page-content">

        {loading && <div className="page-status">Loading prices...</div>}
        {error && <div className="page-status error">{error}</div>}

        {prices && (
          <>
            {/* Price Cards */}
            <div className="kpis">
              <div className="kpi">
                  <span>PLEX</span>
                  <b>{formatPrice(prices.plex.buy_price)}</b>
                  <small>Max Buy (Pack Cost Basis)</small>
                </div>
              <div className="kpi">
                  <span>{prices.jita[`${TYPE_ID_LARGE_SKILL_INJECTOR}`]?.name || 'Large Skill Injector'}</span>
                  <b>{formatPrice(prices.jita[`${TYPE_ID_LARGE_SKILL_INJECTOR}`]?.sell_price)}</b>
                  <small>Jita Min Sell</small>
                </div>
              <div className="kpi">
                  <span>{prices.jita[`${TYPE_ID_SKILL_EXTRACTOR}`]?.name || 'Skill Extractor'}</span>
                  <b>{formatPrice(prices.jita[`${TYPE_ID_SKILL_EXTRACTOR}`]?.sell_price)}</b>
                  <small>Jita Min Sell</small>
                </div>
              {SELLABLE_TYPE_IDS.map(tid => {
                const item = prices.jita[String(tid)];
                return item ? (
                  <div className="kpi" key={tid}>
                  <span>{item.name}</span>
                  <b>{formatPrice(item.sell_price)}</b>
                  <small>Jita Min Sell</small>
                </div>
                ) : null;
              })}
            </div>

            {/* Fee Settings (read-only, configured on Skill Farm page) */}
            <section className="panel">
              <div className="panel-head">
                <h2>Fees</h2>
                <span className="panel-sub">Set on the <Link to="/skillfarm">Skill Farm</Link> page</span>
              </div>
              <div className="panel-body settings-row">
                <FeeSettingsInputs
                  brokerFee={brokerFee}
                  salesTax={salesTax}
                  sccBuy={sccBuy}
                  sccSell={sccSell}
                  disabled
                />
              </div>
            </section>

            {/* Mastery Pack Section */}
            <section className="panel">
              <div className="panel-head">
                <h2>Mastery packs</h2>
                <span className="panel-sub">Monthly cost of each pack, by where you get the extractors</span>
              </div>

              {results.length > 0 && (
                <div className="deal-packs">
                  {MASTERY_PACKS.map(pack => {
                    const rows = packGroups[pack.level] || [];
                    const bestRow = rows.reduce((best, r) =>
                      r.monthlyCost < best.monthlyCost ? r : best, rows[0]);
                    const onSale = packPlex(pack, plexOverrides) !== pack.plex;

                    return (
                      <div key={pack.level} className="deal-pack-card">
                        <div className="pack-header">
                          <h3>{`Level ${pack.level}`} Mastery Pack</h3>
                          <span className="pack-plex">
                            {onSale && <s>{pack.plex.toLocaleString()}</s>}
                            <input
                              type="number"
                              min="1"
                              aria-label={`Level ${pack.level} pack price in PLEX`}
                              value={plexOverrides[pack.level] ?? pack.plex}
                              onChange={e => setPlexOverride(pack.level, e.target.value)}
                            />
                            PLEX
                            {plexOverrides[pack.level] !== undefined && (
                              <button
                                className="btn btn-ghost btn-sm"
                                onClick={() => setPlexOverride(pack.level, null)}
                              >
                                Reset
                              </button>
                            )}
                          </span>
                        </div>

                        <div className="pack-contents">
                          {pack.sp > 0 && <span>{pack.sp.toLocaleString()} SP</span>}
                          <span>{pack.omegaDays} days Omega</span>
                          {pack.skillExtractors > 0 && (
                            <span>{pack.skillExtractors}x Skill Extractor</span>
                          )}
                          {pack.accelerators > 0 && (
                            <span>{pack.accelerators}x Genius 'Boost' Cerebral Accelerator</span>
                          )}
                          {[...pack.sellableItems, ...pack.otherItems].map(item => (
                            <span key={item.name}>{item.qty.toLocaleString()}x {item.name}</span>
                          ))}
                        </div>

                        <div className="pack-breakdown">
                          <div className="breakdown-line">
                            <span>Pack ISK Cost</span>
                            <span>{formatPrice(bestRow.packIskCost)}</span>
                          </div>
                          {bestRow.acceleratorSp > 0 && (
                            <div className="breakdown-line">
                              <span>Accelerator SP ({pack.accelerators} × {ACCELERATOR_DAYS.toFixed(1)} days)</span>
                              <span>{Math.round(bestRow.acceleratorSp).toLocaleString()}</span>
                            </div>
                          )}
                          <div className="breakdown-line">
                            <span>Equivalent Injectors</span>
                            <span>{bestRow.equivalentInjectors.toFixed(1)}</span>
                          </div>
                          <div className="breakdown-line revenue">
                            <span>Injector Revenue</span>
                            <span>{formatPrice(bestRow.injectorRevenue)}</span>
                          </div>
                          {bestRow.extraExtractorRevenue > 0 && (
                            <div className="breakdown-line revenue">
                              <span>Extra Extractor Revenue ({bestRow.extractorsToSell.toFixed(1)}x)</span>
                              <span>{formatPrice(bestRow.extraExtractorRevenue)}</span>
                            </div>
                          )}
                          {bestRow.itemRevenue > 0 && bestRow.itemDetails.map(item => (
                            <div className="breakdown-line revenue" key={item.name}>
                              <span>{item.qty.toLocaleString()}x {item.name}</span>
                              <span>{formatPrice(item.revenue)}</span>
                            </div>
                          ))}
                          <div className="breakdown-line">
                            <span>Omega Months</span>
                            <span>{bestRow.omegaMonths.toFixed(1)}</span>
                          </div>
                        </div>

                        {/* Extractor source comparison */}
                        <div className="pack-extractor-table">
                          <table className="data-table">
                            <thead>
                              <tr>
                                <th>Extractors from</th>
                                <th className="num">Extractor cost</th>
                                <th className="num">Net cost</th>
                                <th className="num">Monthly cost</th>
                              </tr>
                            </thead>
                            <tbody>
                              {rows.map(row => (
                                <tr
                                  key={row.extractorSource}
                                  className={row === bestRow ? 'best-row' : ''}
                                >
                                  <td>{row.extractorSource}</td>
                                  <td className="num">{row.extractorsToBuy > 0 ? formatPrice(row.extractorCost) : '—'}</td>
                                  <td className="num">{formatPrice(row.netCost)}</td>
                                  <td className={`num ${row.monthlyCost <= 0 ? 'text-pos' : ''}`}>
                                    {formatPrice(row.monthlyCost)}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </section>
          </>
        )}
      </div>
    </div>
  );
}

export default DealAnalysisPage;
